import { useState } from 'react';
import { Badge, Button, Field, IconButton, Input, Modal } from '../../../components/ui';
import { maskPhoneDisplay } from '../../../lib/format';
import type { MessageTemplateSummary } from '../types';
import { useFeedbackTemporario } from '../useFeedbackTemporario';

/**
 * Numeros de teste da equipe, so nesta tela da previa (estado local: somem ao
 * recarregar -- a lista persistida chega com o backend, por empresa).
 *
 * Os numeros aparecem MASCARADOS mesmo tendo sido digitados aqui: a tela pode
 * estar aberta num escritorio ou compartilhada. Digitar `11 99999 0000` vira
 * `+5511999990000` (E.164) antes de guardar, para o backend receber o mesmo
 * formato do motor.
 */

const NUMEROS_INICIAIS: readonly string[] = ['+5511999900001', '+5511999900002'];
const MINIMO_DIGITOS = 10;
const MAXIMO_DIGITOS = 13;

export function ModalEnviarTeste({
  open,
  onClose,
  template,
  companyName,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly template: MessageTemplateSummary | null;
  readonly companyName: string;
}): JSX.Element | null {
  const [numeros, setNumeros] = useState<readonly string[]>(NUMEROS_INICIAIS);
  const [novo, setNovo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const { mensagem, mostrar } = useFeedbackTemporario();

  const adicionar = (): void => {
    const digitos = novo.replace(/\D/g, '');
    if (digitos.length < MINIMO_DIGITOS || digitos.length > MAXIMO_DIGITOS) {
      setErro('Informe DDD e número, com 10 a 13 dígitos.');
      return;
    }
    const e164 = digitos.startsWith('55') && digitos.length >= 12 ? `+${digitos}` : `+55${digitos}`;
    if (numeros.includes(e164)) {
      setErro('Este número já está na lista.');
      return;
    }
    setNumeros([...numeros, e164]);
    setNovo('');
    setErro(null);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Enviar teste"
      description="Manda o modelo escolhido para os números de teste salvos. Na prévia nada é enviado."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="primary"
            icon="disparo"
            disabled={numeros.length === 0 || template === null}
            onClick={() => mostrar(`Teste registrado para ${numeros.length} ${numeros.length === 1 ? 'número' : 'números'} — nada foi enviado.`)}
          >
            Enviar teste
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {template === null ? null : (
          <p className="text-sm text-ink-muted">
            Modelo <span className="font-medium text-ink">{template.name}</span> · remetente <span className="font-medium text-ink">{companyName}</span>
          </p>
        )}

        {numeros.length === 0 ? (
          <p className="text-sm text-ink-subtle">Nenhum número de teste salvo.</p>
        ) : (
          <ul className="flex flex-col gap-2" aria-label="Números de teste salvos">
            {numeros.map((n) => (
              <li key={n} className="flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2 text-sm">
                <span className="tabular-nums text-ink">{maskPhoneDisplay(n)}</span>
                <IconButton icon="lixeira" label={`Remover ${maskPhoneDisplay(n)}`} size="sm" onClick={() => setNumeros(numeros.filter((x) => x !== n))} />
              </li>
            ))}
          </ul>
        )}

        <Field label="Adicionar número de teste" hint="DDD + número. Vale só enquanto esta tela estiver aberta; na prévia nada é salvo." error={erro}>
          {({ id, describedBy, invalid }) => (
            <div className="flex gap-2">
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                inputMode="tel"
                autoComplete="off"
                placeholder="11 99999 0000"
                value={novo}
                onChange={(e) => setNovo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    adicionar();
                  }
                }}
              />
              <Button variant="secondary" size="lg" icon="mais" onClick={adicionar}>
                Adicionar
              </Button>
            </div>
          )}
        </Field>

        {mensagem === null ? null : (
          <p role="status" className="flex flex-wrap items-center gap-2 text-sm text-ink">
            <Badge tone="warning" dot>
              Simulado na prévia
            </Badge>
            {mensagem}
          </p>
        )}
      </div>
    </Modal>
  );
}
